import React from 'react';
import { ProgressCircle } from '@americanexpress/dls-react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { IntlProvider } from 'react-intl';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { Helmet } from 'react-helmet';
import type { OneAppV6RootModule } from 'one-app-types';
import { composeModules, LoadModuleData, RenderModule } from 'holocron';
import childRoutes from '../childRoutes';

export type FinancialInsightsToolsRootModuleProps = {
  locale: string
  languageData: Record<string, string>
  children?: React.ReactNode;
}

export type FinancialInsightsToolsRootModuleType =
  OneAppV6RootModule< FinancialInsightsToolsRootModuleProps>;

const FinancialInsightsToolsRoot:
FinancialInsightsToolsRootModuleType = ({
  locale,
  languageData,
  children,
}) => {
  if (Object.entries(languageData).length === 0) {
    return <ProgressCircle />;
  }

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <Helmet>
        <link
          rel="stylesheet"
          href="https://www.aexp-static.com/cdaas/dls/packages/@americanexpress/dls/7.9.0/stylesheets/dls-core.min.css"
        />
        <link rel="icon" href="https://www.americanexpress.com/favicon.ico" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      <RenderModule moduleName="axp-global-header" />
      {children}
      <RenderModule moduleName="axp-footer" />
    </IntlProvider>
  );
};

export const TestableFinancialInsightsToolsRoot = FinancialInsightsToolsRoot;

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
FinancialInsightsToolsRoot.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require, @typescript-eslint/no-require-imports -- require needs to be inside browser check
  FinancialInsightsToolsRoot.appConfig = require('../appConfig').default;
}

export const loadModuleData: LoadModuleData<FinancialInsightsToolsRootModuleProps> = async ({
  store,
}) => {
  const { dispatch } = store;
  await Promise.all([
    dispatch(
      loadLanguagePack('financial-insights-tools-root', { fallbackLocale: 'en-US' })
    ),
    dispatch(composeModules([
      { name: 'axp-global-header' },
      { name: 'axp-footer' },
    ])),
  ]);
};

FinancialInsightsToolsRoot.holocron = {
  name: 'financial-insights-tools-root',
  loadModuleData,
};

export default oneAppModuleWrapper('financial-insights-tools-root')(FinancialInsightsToolsRoot);
